/**
 * The task drawer, read-only by design.
 *
 * Todoist owns tasks. Editing a mirrored field here would be worse than
 * useless: the next sync overwrites title, status, dates, priority and labels
 * from Todoist, so an edit made here disappears without a word. This panel
 * therefore shows those fields and sends you to Todoist to change them.
 *
 * Three things ARE actionable, because Todoist cannot express them or because
 * they are the one action worth taking from a plan:
 *   - completion, which writes back to Todoist (see useToggleItem)
 *   - the prerequisite link, which Todoist has no concept of
 *   - whether the task appears on the client report
 */

import { useMemo, useState } from "react";

import { useToast } from "@/context/ToastContext";
import { useToggleItem, useUpdateItem } from "@/hooks/useData";
import { QUICK_MINUTES } from "@/components/TaskTimeControls";
import { PlayIcon } from "@/components/NavIcons";
import { useTaskTime } from "@/hooks/useTaskTime";
import { shortDate } from "@/lib/format";
import { withLinks } from "@/lib/links";
import { hours, parseDuration } from "@/lib/timeRollup";
import { estimateLabel } from "@/lib/workBlocks";
import { PRIORITY_META, STATUS_META, TYPE_META, type Item, type Phase, type Project } from "@/lib/types";

import { todoistTaskUrl as todoistUrl } from "@/lib/todoistLinks";

export function TaskPanel({ item, project, phase, siblings, onOpenItem, onClose }: {
  item: Item;
  project?: Project;
  phase?: Phase;
  siblings: Item[];
  onOpenItem: (i: Item) => void;
  onClose: () => void;
}) {
  const toggleDone = useToggleItem();
  const updateItem = useUpdateItem();
  const time = useTaskTime(item);

  const prereq = useMemo(
    () => (item.dependsOn ? siblings.find((s) => s.id === item.dependsOn) : undefined),
    [item.dependsOn, siblings],
  );
  const done = item.status === "done";

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={sheet}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 4 }}>
          <span style={{ color: TYPE_META[item.type].color, fontSize: 15, marginTop: 3 }}>
            {TYPE_META[item.type].glyph}
          </span>
          <h2 style={{
            margin: 0, fontSize: 18, fontWeight: 500, flex: 1, letterSpacing: "-0.01em",
            textDecoration: done ? "line-through" : "none",
            color: done ? "var(--muted)" : "var(--ink)",
            overflowWrap: "anywhere",
          }}>
            {withLinks(item.title, item.id)}
          </h2>
          <button onClick={onClose} style={iconBtn} aria-label="Close">✕</button>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0 16px" }}>
          <Chip bg={STATUS_META[item.status].bg} color={STATUS_META[item.status].text}>
            {STATUS_META[item.status].label}
          </Chip>
          <Chip>{TYPE_META[item.type].label}</Chip>
          <Chip color={PRIORITY_META[item.priority].color}>{PRIORITY_META[item.priority].label}</Chip>
          {item.isRecurring && <Chip>repeats</Chip>}
          {(item.labels ?? []).map((l) => <Chip key={l}>@{l}</Chip>)}
        </div>

        {/* The description is where a link most often lives — Todoist's own UI
            puts one there — so it gets the same treatment as the title. */}
        {item.notes && (
          <p style={{ fontSize: 13.5, color: "var(--ink2)", whiteSpace: "pre-wrap", overflowWrap: "anywhere", margin: "0 0 16px" }}>
            {withLinks(item.notes, `${item.id}-n`)}
          </p>
        )}

        <dl style={{ margin: "0 0 18px", display: "grid", gridTemplateColumns: "auto 1fr", gap: "7px 16px", fontSize: 13 }}>
          <Term>Project</Term><Def>{project?.name ?? "—"}</Def>
          <Term>Phase</Term><Def>{phase?.name ?? "—"}</Def>
          {/* Todoist's date is when you plan to work on it; its deadline is the
              hard finish. Labelled that way so the mapping isn't a mystery. */}
          <Term>Start</Term><Def>{item.start ? shortDate(item.start) : "not scheduled"}</Def>
          <Term>Deadline</Term><Def>{item.due ? shortDate(item.due) : "none"}</Def>
          {item.completedAt && (
            <><Term>Completed</Term><Def>{shortDate(item.completedAt.slice(0, 10))}</Def></>
          )}
        </dl>

        {/* Time gets its own block rather than one button among several.
            The row controls on a list are hover-revealed and easy to miss, and
            this drawer is what EVERY surface opens — the board, the grid, the
            calendar, search — so it is the one place per-task timing and
            logging can be relied on to be. */}
        {project && <TimeBlock item={item} time={time} />}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
          <button onClick={() => void toggleDone(item)} style={done ? ghostBtn : primaryBtn}>
            {done ? "Reopen" : "Mark complete"}
          </button>
          {item.todoistId && (
            <a href={todoistUrl(item.todoistId)} target="_blank" rel="noreferrer" style={{ ...ghostBtn, textDecoration: "none" }}>
              Edit in Todoist ↗
            </a>
          )}
        </div>

        <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14 }}>
          <div style={{ fontSize: 11, color: "var(--muted2)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            Project Hub only — not in Todoist
          </div>

          <label style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
            Waits for
            <select
              value={item.dependsOn ?? ""}
              onChange={(e) => updateItem.mutate({ id: item.id, dependsOn: e.target.value || undefined })}
              style={input}
            >
              <option value="">— Nothing</option>
              {siblings.filter((s) => s.id !== item.id).map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
            {prereq && prereq.status !== "done" && (
              <span style={{ display: "block", fontSize: 11.5, color: "var(--warn)", marginTop: 5 }}>
                Blocked until “{prereq.title}” is done.{" "}
                <button onClick={() => onOpenItem(prereq)} style={linkBtn}>Open it</button>
              </span>
            )}
          </label>

          <label style={{ display: "block", fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
            Estimate (h:mm)
            <input
              key={item.estimateHours ?? 0}
              defaultValue={estimateLabel(item.estimateHours)}
              onBlur={(e) => {
                const min = e.target.value.trim() ? parseDuration(e.target.value) : 0;
                if (min == null) { e.target.value = estimateLabel(item.estimateHours); return; }
                if (min === Math.round((item.estimateHours || 0) * 60)) return;
                // null, not undefined, to clear: undefined is dropped from the
                // request and the old estimate would silently stay.
                updateItem.mutate({ id: item.id, estimateHours: min > 0 ? min / 60 : (null as unknown as number) });
              }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
              placeholder="—"
              style={input}
            />
            <span style={{ display: "block", fontSize: 11.5, color: "var(--muted2)", marginTop: 5 }}>
              Drives the Roadmap's forward load. Todoist has no field for this.
            </span>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13, color: "var(--ink2)" }}>
            <input
              type="checkbox"
              checked={Boolean(item.clientVisible)}
              onChange={(e) => updateItem.mutate({ id: item.id, clientVisible: e.target.checked })}
            />
            Show on the client report
          </label>
        </div>
      </div>
    </div>
  );
}

const Term = ({ children }: { children: React.ReactNode }) =>
  <dt style={{ color: "var(--muted)" }}>{children}</dt>;
const Def = ({ children }: { children: React.ReactNode }) =>
  <dd style={{ margin: 0, color: "var(--ink2)" }}>{children}</dd>;

/**
 * Start a timer, or log what already happened.
 *
 * Both, because they are different situations: the timer is for work starting
 * now, and the chips are for the half hour you have just finished and would
 * otherwise never record. The custom box takes 1:30, 90 or 1.5h.
 */
function TimeBlock({ item, time }: { item: Item; time: ReturnType<typeof useTaskTime> }) {
  const toast = useToast();
  const [custom, setCustom] = useState("");

  return (
    <div style={{
      border: "1px solid var(--line)", borderRadius: 10, padding: 12, marginBottom: 16,
      background: time.timingThis ? "var(--accentTint2)" : "var(--surface2)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{
          flex: 1, fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase",
          color: time.timingThis ? "var(--accent)" : "var(--muted2)",
        }}>{time.timingThis ? "● Timing this task" : "Time"}</span>
        {time.loggedSeconds > 0 && (
          <span className="mono" style={{ fontSize: 12, color: "var(--ink2)" }}>
            {hours(time.loggedSeconds)} h logged
          </span>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <button onClick={time.toggleTimer} style={time.timingThis ? primaryBtn : ghostBtn}>
          {time.timingThis
            ? "Stop & log"
            : time.timerRunning ? "Switch timer here" : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <PlayIcon size={13} /> Start timer
              </span>
            )}
        </button>

        <span style={{ width: 1, height: 20, background: "var(--line2)", margin: "0 2px" }} />

        {QUICK_MINUTES.map((m) => (
          <button
            key={m}
            onClick={() => void time.logMinutes(m)}
            title={`Log ${m < 60 ? `${m} minutes` : "an hour"} against this task`}
            style={quickBtn}
          >
            {m < 60 ? `${m}m` : `${m / 60}h`}
          </button>
        ))}
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            const mins = parseDuration(custom);
            if (mins === null || mins <= 0) { toast("Try 1:30, 90 or 1.5h"); return; }
            void time.logMinutes(mins);
            setCustom("");
          }}
          placeholder="1:30"
          aria-label={`Log a custom duration against ${item.title}`}
          className="mono"
          style={{
            width: 62, padding: "5px 8px", borderRadius: 999, fontSize: 12, textAlign: "center",
            border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
          }}
        />
      </div>
    </div>
  );
}

const quickBtn: React.CSSProperties = {
  padding: "5px 11px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12, cursor: "pointer",
};

function Chip({ children, bg, color }: { children: React.ReactNode; bg?: string; color?: string }) {
  return (
    <span style={{
      padding: "2px 9px", borderRadius: 20, fontSize: 11,
      background: bg ?? "var(--tint)", color: color ?? "var(--ink3)",
    }}>{children}</span>
  );
}

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "var(--scrim)", zIndex: 60,
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "60px 20px", overflowY: "auto",
};
const sheet: React.CSSProperties = {
  width: 520, maxWidth: "100%", background: "var(--raised)", borderRadius: 14,
  padding: "22px 26px 24px", border: "1px solid var(--overlayLine)", boxShadow: "var(--shadowOverlay)",
};
const input: React.CSSProperties = {
  width: "100%", padding: "7px 10px", borderRadius: 8, fontSize: 13, marginTop: 5,
  border: "1px solid var(--line2)", background: "var(--surface)", color: "var(--ink)",
};
const primaryBtn: React.CSSProperties = {
  padding: "8px 16px", borderRadius: 999, border: "none",
  background: "var(--accent)", color: "var(--onAccent)", fontSize: 12.5, cursor: "pointer",
};
const ghostBtn: React.CSSProperties = {
  padding: "8px 14px", borderRadius: 999, border: "1px solid var(--line2)",
  background: "var(--surface)", color: "var(--ink2)", fontSize: 12.5, cursor: "pointer",
  display: "inline-flex", alignItems: "center",
};
const linkBtn: React.CSSProperties = {
  background: "none", border: "none", padding: 0, color: "var(--accent)",
  cursor: "pointer", fontSize: 11.5, textDecoration: "underline",
};
const iconBtn: React.CSSProperties = {
  border: "none", background: "none", color: "var(--muted2)", cursor: "pointer", fontSize: 14,
};
